import { useMemo, useState } from 'react';
import { Download, Search, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useRenotifyUnresponded } from 'hooks/useNotification';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import {
  ALL_FILTER,
  attemptStatusLabel,
  channelLabel,
  channelOptions,
  DeliveryAttemptDto,
  DeliveryStatus,
  EMPTY_DELIVERY_FILTERS,
  filterRecipients,
  NotificationDeliveryReportDto,
  NotificationRecipientDto,
  RECIPIENT_STATE_LABEL,
  RecipientDeliveryState,
  recipientResponse,
  FOLLOW_UP_STATUS_LABEL,
  recipientStateLabel,
  stateOptions,
  summaryCards,
  SummaryCard,
  teamOptions,
  toDeliveryCsv,
} from './notificationDeliveryModel';

const TONE_CLASS: Record<SummaryCard['tone'], string> = {
  neutral: 'tw:text-foreground',
  good: 'tw:text-emerald-500',
  warn: 'tw:text-amber-500',
  bad: 'tw:text-destructive',
};

const STATE_VARIANT: Record<
  RecipientDeliveryState,
  'green' | 'blue' | 'amber' | 'destructive' | 'outline'
> = {
  [RecipientDeliveryState.ACKNOWLEDGED]: 'green',
  [RecipientDeliveryState.DELIVERED]: 'blue',
  [RecipientDeliveryState.SENT]: 'amber',
  [RecipientDeliveryState.FAILED]: 'destructive',
  [RecipientDeliveryState.UNREACHABLE]: 'outline',
};

const ATTEMPT_VARIANT: Record<
  DeliveryStatus,
  'green' | 'blue' | 'destructive' | 'outline'
> = {
  [DeliveryStatus.DELIVERED]: 'green',
  [DeliveryStatus.SENT]: 'blue',
  [DeliveryStatus.QUEUED]: 'outline',
  [DeliveryStatus.FAILED]: 'destructive',
  [DeliveryStatus.BOUNCED]: 'destructive',
  [DeliveryStatus.UNDELIVERABLE]: 'outline',
};

function downloadCsv(filename: string, contents: string) {
  const url = URL.createObjectURL(
    new Blob([contents], { type: 'text/csv;charset=utf-8;' })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Who a sent notification reached, on which channel, and who has responded.
 *
 * The report body, without a container, so the sender's modal and the sends
 * dashboard's detail page render exactly the same thing rather than drifting
 * into two versions of the same table.
 *
 * Filtering is by channel, status and strike team. AOR is intentionally absent
 * here: `notification_aors` records which AORs a notification targeted, but a
 * recipient's own coverage is a separate concept, so there is no
 * recipient-level AOR to filter on.
 */
export function NotificationDeliveryReport({
  notificationId,
  data,
  isLoading,
  isError,
}: {
  notificationId: string;
  data: NotificationDeliveryReportDto | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const renotify = useRenotifyUnresponded();
  const [filters, setFilters] = useState(EMPTY_DELIVERY_FILTERS);

  // A fresh `?? []` on every render would defeat every memo below it.
  const recipients = useMemo(() => data?.recipients ?? [], [data]);
  const visible = useMemo(
    () => filterRecipients(recipients, filters),
    [recipients, filters]
  );
  const channels = useMemo(() => channelOptions(recipients), [recipients]);
  const teams = useMemo(() => teamOptions(recipients), [recipients]);
  const states = useMemo(() => stateOptions(recipients), [recipients]);
  const cards = useMemo(
    () =>
      data
        ? summaryCards(data.summary, data.acknowledgementRequired, recipients)
        : [],
    [data, recipients]
  );

  const awaiting = data?.summary.awaitingResponse ?? 0;
  const notReached =
    (data?.summary.failed ?? 0) + (data?.summary.unreachable ?? 0);
  const canRenotify = awaiting + notReached > 0;
  const bulkPending =
    renotify.isPending && !renotify.variables?.recipientUserGuid;

  if (isLoading) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        Loading delivery report…
      </p>
    );
  }

  if (isError) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-destructive">
        Could not load the delivery report.
      </p>
    );
  }

  if (recipients.length === 0) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        No delivery records for this notification. Sends made before delivery
        tracking was enabled have no per-recipient history.
      </p>
    );
  }

  return (
    <div className="tw:space-y-3">
      <dl className="tw:m-0 tw:grid tw:grid-cols-2 tw:gap-2 tw:sm:grid-cols-3 tw:lg:grid-cols-6">
        {cards.map((card) => (
          <div
            key={card.label}
            className="tw:rounded-md tw:border tw:border-border tw:px-3 tw:py-2"
          >
            <dt className="tw:text-[11px] tw:text-muted-foreground">
              {card.label}
            </dt>
            <dd
              className={`tw:m-0 tw:text-lg tw:font-medium ${TONE_CLASS[card.tone]}`}
            >
              {card.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="tw:grid tw:gap-2 tw:sm:grid-cols-2 tw:lg:grid-cols-4">
        <div className="tw:relative">
          <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
          <Input
            value={filters.search}
            onChange={(event) =>
              setFilters((prev) => ({
                ...prev,
                search: event.target.value,
              }))
            }
            placeholder="Search recipients…"
            aria-label="Search recipients"
            className="tw:h-8 tw:pl-8 tw:text-xs"
          />
        </div>
        <FilterSelect
          label="Status"
          value={filters.state}
          allLabel="All statuses"
          options={states.map((state) => ({
            value: state,
            label: RECIPIENT_STATE_LABEL[state],
          }))}
          onChange={(state) => setFilters((prev) => ({ ...prev, state }))}
        />
        <FilterSelect
          label="Channel"
          value={filters.channel}
          allLabel="All channels"
          options={channels.map((channel) => ({
            value: channel,
            label: channelLabel(channel),
          }))}
          onChange={(channel) => setFilters((prev) => ({ ...prev, channel }))}
        />
        <FilterSelect
          label="Team"
          value={filters.teamId}
          allLabel="All teams"
          options={teams.map((team) => ({
            value: team.id,
            label: team.name,
          }))}
          onChange={(teamId) => setFilters((prev) => ({ ...prev, teamId }))}
        />
      </div>

      <div className="tw:flex tw:flex-wrap tw:gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={visible.length === 0}
          onClick={() =>
            downloadCsv(
              `notification-${notificationId}-deliveries.csv`,
              toDeliveryCsv(visible)
            )
          }
        >
          <Download className="tw:h-4 tw:w-4" />
          Export CSV
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canRenotify || bulkPending}
          title={
            canRenotify
              ? undefined
              : 'Everyone has responded, so there is nobody to chase.'
          }
          onClick={() => void renotify.mutateAsync({ notificationId })}
        >
          <Send className="tw:h-4 tw:w-4" />
          {bulkPending ? 'Re-sending…' : 'Re-notify non-responders'}
        </Button>
      </div>

      <div className="tw:overflow-x-auto tw:rounded-md tw:border tw:border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Recipient</TableHead>
              <TableHead className="tw:w-[140px]">Status</TableHead>
              <TableHead className="tw:min-w-[240px]">Attempts</TableHead>
              <TableHead className="tw:w-[130px]">Response</TableHead>
              {data?.hasFollowUp ? (
                <TableHead className="tw:w-[130px]">Step 2</TableHead>
              ) : null}
              <TableHead className="tw:w-[160px]">Responded</TableHead>
              <TableHead className="tw:w-[100px]">
                <span className="tw:sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={data?.hasFollowUp ? 7 : 6}
                  className="tw:text-xs tw:text-muted-foreground"
                >
                  No recipients match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              visible.map((recipient) => (
                <RecipientRow
                  key={recipient.userGuid}
                  recipient={recipient}
                  showFollowUp={Boolean(data?.hasFollowUp)}
                  bulkPending={bulkPending}
                  pendingUserGuid={
                    renotify.isPending
                      ? renotify.variables?.recipientUserGuid
                      : undefined
                  }
                  onResend={(recipientUserGuid) =>
                    void renotify.mutateAsync({
                      notificationId,
                      recipientUserGuid,
                    })
                  }
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/**
 * Labels a cascade attempt with the rung it belongs to.
 *
 * Step 0 is the original send and is left unlabelled, so the ordinary
 * single-send case reads exactly as it did before cascades existed and only an
 * escalation carries the extra marker.
 */
function attemptLabel(attempt: DeliveryAttemptDto): string {
  const channel = channelLabel(attempt.channel);
  return attempt.escalationStep > 0
    ? `${channel} · step ${attempt.escalationStep}`
    : channel;
}

function RecipientRow({
  recipient,
  showFollowUp,
  bulkPending,
  pendingUserGuid,
  onResend,
}: {
  recipient: NotificationRecipientDto;
  showFollowUp: boolean;
  bulkPending: boolean;
  pendingUserGuid?: string;
  onResend: (recipientUserGuid: string) => void;
}) {
  const respondedAt = recipient.respondedAt ?? recipient.acknowledgedAt;
  const response = recipientResponse(recipient);
  const canResendRow = recipient.state !== RecipientDeliveryState.ACKNOWLEDGED;
  const rowPending = pendingUserGuid === recipient.userGuid;

  // Oldest first, so a cascade reads down the list as it unfolded.
  const attempts = [...recipient.attempts].sort(
    (a, b) => a.escalationStep - b.escalationStep
  );

  return (
    <TableRow>
      <TableCell className="tw:align-top">
        <div className="tw:flex tw:flex-col tw:gap-0.5">
          <span className="tw:text-sm tw:leading-snug">{recipient.name}</span>
          {recipient.email ? (
            <span className="tw:text-[11px] tw:text-muted-foreground">
              {recipient.email}
            </span>
          ) : null}
          {recipient.teams.length > 0 ? (
            <span className="tw:text-[11px] tw:text-muted-foreground">
              {recipient.teams.map((team) => team.name).join(', ')}
            </span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="tw:align-top">
        <Badge
          variant={STATE_VARIANT[recipient.state]}
          className="tw:w-fit tw:text-[10px]"
        >
          {recipientStateLabel(recipient)}
        </Badge>
      </TableCell>
      <TableCell className="tw:align-top">
        <div className="tw:flex tw:flex-wrap tw:gap-1">
          {attempts.map((attempt) => (
            <Badge
              key={`${attempt.channel}-${attempt.escalationStep}`}
              variant={ATTEMPT_VARIANT[attempt.status]}
              className="tw:text-[10px]"
              // The reason a channel failed is the whole point of this table,
              // so it must be reachable without another click.
              title={[
                `${channelLabel(attempt.channel)}: ${attemptStatusLabel(attempt)}`,
                attempt.escalationStep > 0
                  ? `Escalation step ${attempt.escalationStep}`
                  : null,
                attempt.destination,
                attempt.error,
                attempt.sentAt
                  ? `Sent ${formatDateTime(attempt.sentAt)}`
                  : null,
                attempt.deliveredAt
                  ? `Delivered ${formatDateTime(attempt.deliveredAt)}`
                  : null,
              ]
                .filter(Boolean)
                .join('\n')}
            >
              {attemptLabel(attempt)}
            </Badge>
          ))}
        </div>
      </TableCell>
      <TableCell className="tw:align-top">
        {response ? (
          <Badge variant="outline" className="tw:w-fit tw:text-[10px]">
            {response}
          </Badge>
        ) : (
          // Acknowledged in-app or through Teams: confirmed, but with no
          // option attached, which is different from not having answered.
          <span className="tw:text-xs tw:text-muted-foreground">
            {respondedAt ? 'Confirmed' : '—'}
          </span>
        )}
      </TableCell>
      {showFollowUp ? (
        <TableCell className="tw:align-top">
          <span className="tw:text-xs">
            {recipient.followUpStatus
              ? FOLLOW_UP_STATUS_LABEL[recipient.followUpStatus]
              : '—'}
          </span>
        </TableCell>
      ) : null}
      <TableCell className="tw:align-top tw:text-xs tw:text-muted-foreground">
        {respondedAt ? formatDateTime(respondedAt) : '—'}
      </TableCell>
      <TableCell className="tw:align-top">
        {canResendRow ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="tw:h-7 tw:px-2 tw:text-xs"
            disabled={bulkPending || rowPending}
            onClick={() => onResend(recipient.userGuid)}
          >
            {rowPending ? 'Re-sending…' : 'Resend'}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function FilterSelect({
  label,
  value,
  allLabel,
  options,
  onChange,
}: {
  label: string;
  value: string;
  allLabel: string;
  options: { value: string; label: string }[];
  onChange: (next: string) => void;
}) {
  const selected = options.find((option) => option.value === value);

  return (
    <Select
      value={value}
      onValueChange={(next) => onChange(!next ? ALL_FILTER : String(next))}
    >
      <SelectTrigger
        size="sm"
        aria-label={label}
        className="tw:w-full tw:bg-background tw:text-xs tw:text-foreground"
      >
        <SelectValue placeholder={allLabel}>
          {selected?.label ?? allLabel}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="dark">
        <SelectItem value={ALL_FILTER}>{allLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default NotificationDeliveryReport;
